/**
 * AuthDialog Component
 *
 * Dialog for user authentication via LogTo.
 * With LogTo, all auth (email/password, Google, GitHub) happens on
 * LogTo's hosted page. This dialog provides convenient buttons to
 * launch the auth flow directly to a specific provider.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { LogIn, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import type { OAuthProvider } from '../services/authService';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
interface AuthDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional callback when auth succeeds */
  onSuccess?: () => void;
  /**
   * Why the user is signing in — switches the header copy. Most traffic
   * through this dialog is TRIAL intent (field data), but the default copy
   * addressed subscribers only.
   */
  intent?: 'trial' | 'manage';
}

/**
 * Authentication dialog with social login and email options.
 * All auth is handled by LogTo's hosted sign-in page.
 */
export function AuthDialog({ open, onOpenChange, onSuccess, intent = 'manage' }: AuthDialogProps) {
  const { signInWithOAuth, signIn, isLoading, error, clearError, user } = useAuth();

  const [localError, setLocalError] = useState<string | null>(null);
  const displayError = localError || error;
  const didHandleSuccessRef = useRef(false);
  // The user object present at the moment the dialog opened. In the
  // re-authentication / "zombie session" flow the user is STILL signed in when
  // the dialog opens (the OIDC refresh token lapsed, but `user` stays cached),
  // so that initial presence must NOT be treated as a fresh sign-in.
  const userAtOpenRef = useRef<typeof user>(null);
  const wasOpenRef = useRef(false);

  const resetForm = useCallback(() => {
    setLocalError(null);
    clearError();
  }, [clearError]);

  const completeAuth = useCallback(() => {
    if (didHandleSuccessRef.current) return;
    didHandleSuccessRef.current = true;

    resetForm();
    onOpenChange(false);
    onSuccess?.();
  }, [resetForm, onOpenChange, onSuccess]);

  // Close the dialog only when authentication actually CHANGES as a result of
  // opening it: a logged-out user signs in, or a fresh user object replaces the
  // stale one after re-auth. A (stale) user that was already present when the
  // dialog opened must NOT close it — otherwise the re-logon dialog flashes and
  // instantly disappears (the "zombie session" Sign In bug).
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!open) {
      didHandleSuccessRef.current = false;
      return;
    }

    if (justOpened) {
      userAtOpenRef.current = user;
    }

    if (user && user !== userAtOpenRef.current) {
      console.log('[AuthDialog] User authenticated, closing dialog');
      completeAuth();
    }
  }, [open, user, completeAuth]);

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleOAuth = async (provider: OAuthProvider) => {
    setLocalError(null);
    await signInWithOAuth(provider);
    // OAuth flow initiated, dialog stays open while waiting for callback
  };

  const handleEmailSignIn = async () => {
    setLocalError(null);
    // Opens LogTo's sign-in page where user can use email/password
    await signIn('', '');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5 text-primary" />
            {intent === 'trial' ? 'Create a free account' : 'Sign In'}
          </DialogTitle>
          <DialogDescription>
            {intent === 'trial'
              ? 'Sign in or create a free account to start your 14-day Pro trial.'
              : 'Sign in to manage your Pro subscription.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* OAuth Buttons */}
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-center gap-2"
              onClick={() => handleOAuth('google')}
              disabled={isLoading}
              data-testid="google-oauth-button"
            >
              <GoogleIcon className="h-4 w-4" />
              Continue with Google
            </Button>

            <Button
              variant="outline"
              className="w-full justify-center gap-2"
              onClick={() => handleOAuth('github')}
              disabled={isLoading}
              data-testid="github-oauth-button"
            >
              <GitHubIcon className="h-4 w-4" />
              Continue with GitHub
            </Button>
          </div>

          {/*
            Divider. The "Or" span is a MASK, not a chip: it paints the
            dialog's own surface over the middle of the rule behind it so the
            word appears to sit in a gap in the line. That only works while
            its fill exactly matches the surface it sits on — so it tracks
            `bg-surface-raised` (what DialogContent paints, CH-22), not
            `bg-background` (the app canvas). With the two apart on dark it
            drew a faint grey box around "Or" instead of a clean break.
          */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-surface-raised px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          {/* Email sign-in button */}
          <Button
            className="w-full"
            onClick={handleEmailSignIn}
            disabled={isLoading}
            data-testid="submit-button"
          >
            {isLoading ? (
              <>
                <RandomDotmSpinner size={16} className="mr-2" />
                Opening sign in...
              </>
            ) : (
              'Sign In with Email'
            )}
          </Button>

          {/* Error Display */}
          {displayError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{displayError}</span>
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground">
            Don&apos;t have an account? You can create one on the sign-in page.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Simple SVG icons for OAuth providers
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}
